import { Injectable, inject, WritableSignal } from '@angular/core';
import { Router } from '@angular/router'
import { ApiClientConstant, RequestQueryPayload } from '@cureskin/api-client';
import { InstantCheckup, SubmitDefaultAnswersResponse } from '@cureskin/api-client/src/server-api/api';
import { ConnectionService } from '@services/connection-service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { SubmitDefaultAnswersSheetComponent } from '@shared/bottom-sheet-layouts/submit-default-answers-sheet/submit-default-answers-sheet.component';
import { EventLoggerService } from '@services/event-logger-service';
import { LocalStorageService } from '@services/local-storage-service';
import { RegimenService } from '@services/regimen-service/regimen.service';
import { ExperimentService } from '@services/experiment';
import { UserService } from '@services/user';
import { WindowRefService } from '@services/window-ref-service';
import { ChatSkipLastQuestionsExperiment } from '@services/experiment/experiment.service.types';

@Injectable({ providedIn: 'root' })
export class ConsultationSessionService {

  private readonly router: Router = inject(Router);
  private readonly connectionService = inject(ConnectionService);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly eventLoggerService = inject(EventLoggerService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly regimenService = inject(RegimenService);
  private readonly experimentService = inject(ExperimentService);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly userService = inject(UserService);
  private readonly windowRefService = inject(WindowRefService);

  chats: any[] = [];
  consultations: Array<Table.ConsultationSession> = undefined;
  botChats: Table.ConsultationSessionChat[] = [];
  userChats: Table.ConsultationSessionChat[] = [];
  private pendingConsultationSession: Table.ConsultationSession | null = null;
  private readonly lastBotQuestionIdByClassKey = 'lastBotQuestionIdByClass';

  constructor() {}

  getChats(): any {
    return this.chats;
  }

  setChats(chats: Table.ConsultationSessionChat[]): void {
    this.chats = chats;
    this.setBotChats();
    this.setUserChats();
  }

  setBotChats(): void {
    this.botChats = this.chats.filter((chat) => chat.get('Owner') === ApiClientConstant.ChatMessage.Owner.BOT);
  }

  setUserChats(): void {
    this.userChats = this.chats.filter((chat) => chat.get('Owner') === ApiClientConstant.ChatMessage.Owner.USER);
  }

  getBotChats(): Table.ConsultationSessionChat[] {
    return this.botChats;
  }

  getUserChats(): Table.ConsultationSessionChat[] {
    return this.userChats;
  }

  getAllLastBotQuestionIds(): Record<string, string> {
    try {
      return this.localStorageService.getJsonValue(this.lastBotQuestionIdByClassKey) ?? {};
    } catch {
      return {};
    }
  }

  private persistLastBotQuestionIds(lastBotQuestionIdsByClass: Record<string, string>): void {
    try {
      this.localStorageService.setJsonValue(this.lastBotQuestionIdByClassKey, lastBotQuestionIdsByClass);
    } catch {
      // Non-critical write — a storage failure must never break the caller's render path.
    }
  }

  getLastBotQuestionTranslationData(consultationSession?: Table.ConsultationSession): Array<{ regimenClass: string, questionId: string }> {
    const questionIdsByClass = { ...this.getAllLastBotQuestionIds() };
    const currentRegimenClass: string = consultationSession?.get('PrivateMainConcernClass');
    if (currentRegimenClass && !questionIdsByClass[currentRegimenClass]) {
      questionIdsByClass[currentRegimenClass] = this.getBotChats().at(-1)?.get('questionId');
    }
    return Object.entries(questionIdsByClass)
      .filter(([, questionId]: [string, string]): boolean => !!questionId)
      .map(([regimenClass, questionId]: [string, string]): { regimenClass: string, questionId: string } =>
        ({ regimenClass, questionId }));
  }

  setLastBotQuestionId(regimenClass: string, questionId: string): void {
    const lastBotQuestionIdsByClass = this.getAllLastBotQuestionIds();
    lastBotQuestionIdsByClass[regimenClass] = questionId;
    this.persistLastBotQuestionIds(lastBotQuestionIdsByClass);
  }

  getLastBotQuestionId(regimenClass: string): string | undefined {
    return this.getAllLastBotQuestionIds()[regimenClass];
  }

  clearLastBotQuestionId(regimenClass: string): void {
    const lastBotQuestionIdsByClass = this.getAllLastBotQuestionIds();
    delete lastBotQuestionIdsByClass[regimenClass];
    this.persistLastBotQuestionIds(lastBotQuestionIdsByClass);
  }

  setConsultations(consultations: Array<Table.ConsultationSession>): void {
    this.consultations = consultations;
  }

  async getConsultations(user: Table.User): Promise<Array<Table.ConsultationSession>> {
    if (Array.isArray(this.consultations)) return this.consultations;
    return await this.fetchForUser(user);
  }

  async fetchForUser(user: Table.User): Promise<Array<Table.ConsultationSession>> {
    const payload: RequestQueryPayload<Table.ConsultationSession> = {
      where: {
        user,
        PrivateMainConcern: { $exists: true },
        PrivateTreeCompleted: false,
        archive: false,
        regimen: { $exists: false },
      },
      descending: 'updatedAt',
    };

    return this.connectionService.fetchConsultationSessions(payload);
  }

  findIncompleteFaceConsultationPresent(consultations: Array<Table.ConsultationSession>): Table.ConsultationSession {
    return consultations.find((consultation) => !consultation.get('PrivateTreeCompleted') &&
      consultation.get('PrivateMainConcernClass') === ApiClientConstant.Regimen.Class.FACE);
  }

  findIncompleteHairConsultationPresent(consultations: Array<Table.ConsultationSession>): Table.ConsultationSession {
    return consultations.find((consultation) => !consultation.get('PrivateTreeCompleted') &&
      consultation.get('PrivateMainConcernClass') === ApiClientConstant.Regimen.Class.HAIR);
  }

  /** True when the acting user has an incomplete FACE or HAIR consultation session pending. */
  async isIncompleteChatPending(): Promise<boolean> {
    const user = this.connectionService.getActingUser();
    if (!user) return false;

    const consultations = await this.getConsultations(user);
    const incompleteFaceConsultation = this.findIncompleteFaceConsultationPresent(consultations);
    const incompleteHairConsultation = this.findIncompleteHairConsultationPresent(consultations);

    return !!incompleteFaceConsultation || !!incompleteHairConsultation;
  }

  shouldShowSubmitDefualtAnswerSheet(consultationSession: Table.ConsultationSession, lastBotChat: Table.ConsultationSessionChat, chatSkipLastQuestionsExperiment: ChatSkipLastQuestionsExperiment): boolean {
    if (!chatSkipLastQuestionsExperiment?.enable) {
      return false;
    }
    const questionIdFromVariant = (chatSkipLastQuestionsExperiment?.variant as unknown as { questionIdToSkipAt: string })?.questionIdToSkipAt;
    if (lastBotChat?.get('questionId') !== questionIdFromVariant) {
      return false;
    }

    const privateMainConcernClass = consultationSession?.get('PrivateMainConcernClass');
    const consultationsDetails: Array<{
      objectId: string;
      privateMainConcernClass: string;
      isSubmitDefaultAnswerSheetShow: boolean;
    }> = this.localStorageService.getJsonValue('consultationsDetails', '[]');

    const consultationDetail = consultationsDetails.find(
      (item) => item.privateMainConcernClass === privateMainConcernClass
    );

    return !consultationDetail?.isSubmitDefaultAnswerSheetShow;
  }

  async submitDefaultAnswersForConsultation(consultationSession: Table.ConsultationSession): Promise<SubmitDefaultAnswersResponse> {
    const consultationSessionId = consultationSession?.id;
    const result = await this.connectionService.submitDefaultAnswersForConsultation(consultationSessionId);
    return result;
  }

  getPendingConsultationSession(): Table.ConsultationSession | null {
    const session = this.pendingConsultationSession;
    this.pendingConsultationSession = null;
    return session;
  }

  setPendingConsultationSession(consultationSession: Table.ConsultationSession): void {
    this.pendingConsultationSession = consultationSession;
  }

  /** User-level pre-check for the consultation quiz UI (`consultation_quiz_ui`): unpaid, non-internal user. */
  async isUserEligibleForConsultationQuiz(): Promise<boolean> {
    if (this.windowRefService.isRenderedInIframe() || this.connectionService.isInternalUser()) {
      return false;
    }
    await this.experimentService.experimentsLoaded;
    if (!this.experimentService.isConsultationQuizEnabled()) return false;
    const user = this.connectionService.getActingUser();
    if (!user || user.get('type')) return false;
    return !this.userService.isPaidUser(user);
  }

  /**
   * A session that already produced a regimen. The api writes both fields in one update when it
   * allocates, and checks them the same way (`consultation-chatbot.service`).
   */
  isConsultationCompleted(session: Table.ConsultationSession | null | undefined): boolean {
    return !!(session?.get('PrivateRegimenGiven') || session?.get('PrivateTreeCompleted'));
  }

  /** Full quiz-eligibility check for a concern class (FACE/HAIR). */
  async shouldOpenConsultationQuiz(concernClass: string): Promise<boolean> {
    if (!concernClass) return false;
    if (!(await this.isUserEligibleForConsultationQuiz())) return false;
    return this.experimentService.isConsultationQuizEnabledForClass(concernClass);
  }

  /**
   * Opens the skip consultation questions bottomsheet if conditions are met.
   * @param consultationSession - The consultation session object
   * @returns MatBottomSheetRef if sheet was opened, null otherwise
   */
  validateAndOpenSkipConsultationQuestionsSheet(
    userFirstName: string,
    chatSkipLastQuestionsExperiment: ChatSkipLastQuestionsExperiment,
    consultationSession: Table.ConsultationSession,
  ): MatBottomSheetRef<SubmitDefaultAnswersSheetComponent> | null {
    const botChats = this.getBotChats();
    const lastBotChat = botChats.at(-1);
    const shouldShowSubmitDefualtAnswerSheet = this.shouldShowSubmitDefualtAnswerSheet(consultationSession, lastBotChat, chatSkipLastQuestionsExperiment);
    if (!shouldShowSubmitDefualtAnswerSheet) {
      return null;
    }

    const submitDefaultAnswersSheetRef = this.bottomSheet.open(SubmitDefaultAnswersSheetComponent, {
      data: {
        userFirstName,
        privateMainConcernClass: consultationSession.get('PrivateMainConcernClass'),
        handleViewReportClick: async (isLoading: WritableSignal<boolean>): Promise<void> => {
          isLoading.set(true);
          this.setPendingConsultationSession(consultationSession);
          submitDefaultAnswersSheetRef.dismiss();
          this.navigateToSubmitDefaultAnswers();
        },
        handleCompleteConsultationClick: (isLoading: WritableSignal<boolean>): void => {
          isLoading.set(false);
          submitDefaultAnswersSheetRef.dismiss();
        },
        handleCancelClick: () => {
          this.bottomSheet.dismiss();
          this.navigateToHome();
        },
      },
      backdropClass: 'bottomsheet-blur-overlay-sm',
    });

    return submitDefaultAnswersSheetRef;
  }


  async submitDefaultAnswersAndNavigateToRegimenPage(consultationSession: Table.ConsultationSession, userAge: string): Promise<void> {
    if (!consultationSession) {
      this.navigateToHome();
      return;
    }
    const privateMainConcernClass = consultationSession?.get('PrivateMainConcernClass');
    let result: SubmitDefaultAnswersResponse | null = null;
    try {
      result = await this.submitDefaultAnswersForConsultation(consultationSession);
    } catch (error) {
      this.eventLoggerService.trackEvent('ERROR_submitDefaultAnswersForConsultation', { error: error, consultationSessionId: consultationSession?.id });
      this.navigateToHome();
      return;
    }

    this.regimenService.sendRegimenAllocatedEventForIOS(userAge);

    if ((result as any)?.regimenId) {
      this.navigateToReport(privateMainConcernClass);
    } else {
      this.navigateToHome();
    }
  }

  triggerAiReportAnalysis(regimenClass: string, answeredQuestionId: string): void {
    if (!this.experimentService.shouldTriggerAiReportAnalysis(answeredQuestionId, regimenClass)) return;
    this.connectionService.generateHairReport().catch((): void => {});
  }

  /**
   * Tree-end regimen-allocation signal — the `showAfterTime` delay message ("your regimen will
   * be ready shortly"). The exact identifier legacy chatV2 keys on (addToChats); callers must
   * evaluate it per message across the batch, not just on the latest row (the allocated-regimen
   * ARTICLE usually lands after it).
   */
  isRegimenAllocationMessage(message: Table.ConsultationSessionChat | null): boolean {
    return !!message?.get('showAfterTime');
  }

  /**
   * Regimen-allocation side effects for a consultation completion message (`showAfterTime`) —
   * mirrors the legacy chatV2 addToChats block, minus its in-chat countdown bubble (the regimen
   * preparation page owns the visible timer via regimen.activeFrom). Returns false for internal
   * users, for whom every side effect is skipped (chatV2 parity) — the caller decides navigation.
   */
  handleRegimenAllocation(
    completionMessage: Table.ConsultationSessionChat | null,
    user: Table.User,
    consultationSession: Table.ConsultationSession | null,
  ): boolean {
    if (this.connectionService.isInternalUser()) return false;
    this.regimenService.sendRegimenAllocatedEventForIOS(user?.get('Age'));
    const showAfterTime = completionMessage?.get('showAfterTime');
    const timeLeft = showAfterTime ? new Date(showAfterTime).getTime() - new Date().getTime() : 0;
    if (timeLeft > 0) {
      const userConcernClass = user?.get('PrivateMainConcernClass');
      this.connectionService.updateExperimentLogsInUser({ regimen_allocation_timer_started: userConcernClass });
      this.eventLoggerService.trackEvent('regimen_allocation_timer_started', { class: userConcernClass });
    }
    this.localStorageService.delete('sideFaceUploadSkipped');
    this.navigateToReport(consultationSession?.get('PrivateMainConcernClass'));
    return true;
  }

  navigateToReport(privateMainConcernClass: string): void {
    this.router.navigate(['/regimen'], {
      queryParams: {
        tab: 'regimen',
        class: privateMainConcernClass,
        showTimer: true,
      }
    })
  }

  navigateToHome(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  navigateToSubmitDefaultAnswers(): void {
    this.router.navigate(['/submit-default-answers']);
  }

  updateConsultationDetailsInLocalStorage(consultationSession: Table.ConsultationSession): void {
    if (!consultationSession) return;
    const privateMainConcernClass = consultationSession.get('PrivateMainConcernClass');
    if (!privateMainConcernClass) return;

    const consultationsDetails: Array<{
      objectId: string;
      privateMainConcernClass: string;
      isSubmitDefaultAnswerSheetShow: boolean;
    }> = this.localStorageService.getJsonValue('consultationsDetails', '[]');

    const selectedClassIndex = consultationsDetails.findIndex(
      (item) => item.privateMainConcernClass === privateMainConcernClass
    );

    const isSubmitDefaultAnswerSheetShow = selectedClassIndex === -1
      ? false
      : consultationsDetails[selectedClassIndex].isSubmitDefaultAnswerSheetShow;

    const updatedData = {
      objectId: consultationSession?.id,
      privateMainConcernClass,
      isSubmitDefaultAnswerSheetShow,
    };

    if (selectedClassIndex === -1) {
      consultationsDetails.push(updatedData);
    } else {
      consultationsDetails[selectedClassIndex] = updatedData;
    }

    this.localStorageService.setJsonValue('consultationsDetails', consultationsDetails);
  }

  deleteConsultationDetailsForClass(privateMainConcernClass: string): void {
    if (!privateMainConcernClass) return;

    const consultationsDetails: Array<{
      objectId: string;
      privateMainConcernClass: string;
      isSubmitDefaultAnswerSheetShow: boolean;
    }> = this.localStorageService.getJsonValue('consultationsDetails', '[]');

    const filteredDetails = consultationsDetails?.filter(
      (item) => item.privateMainConcernClass !== privateMainConcernClass
    );

    this.localStorageService.setJsonValue('consultationsDetails', filteredDetails);
  }

  async shouldShowSubmitDefualtAnswersBanner(user: Table.User): Promise<boolean> {
    const chatSkipLastQuestionExperiment = this.experimentService.getChatSkipLastQuestionHomeBanner();
    if (!chatSkipLastQuestionExperiment?.enable) {
      return false;
    }
    const activeConsultations = await this.getConsultations(user);
    const faceConsultation = this.getFaceConsultation(activeConsultations);
    if(!faceConsultation) {
      return false;
    }
    const payload: RequestQueryPayload<Table.ConsultationSessionChat> = {
      where: {
        consultationSession: faceConsultation,
        Owner: ApiClientConstant.ChatMessage.Owner.BOT,
      },
      limit: 1,
      project: ['Owner', 'questionId', 'objectId'],
      descending: 'createdAt',
    }
    const botquestions = await this.getConsultationSessionChats(payload);
    const lastBotQuestion = botquestions[0];


    if (!this.isValidQuestionIdToSkip(lastBotQuestion?.get('questionId'), chatSkipLastQuestionExperiment.variant)) {
      return false;
    }
    return true;
  }

  async getConsultationSessionChats(payload: RequestQueryPayload<Table.ConsultationSessionChat>): Promise<Array<Table.ConsultationSessionChat>> {
    return await this.connectionService.findConsultationSessionChat(payload);
  }

  getFaceConsultation(consultations: Array<Table.ConsultationSession>): Table.ConsultationSession {
    return consultations.find((consultation) => consultation.get('PrivateMainConcernClass') === ApiClientConstant.Regimen.Class.FACE);
  }

  getHairConsultation(consultations: Array<Table.ConsultationSession>): Table.ConsultationSession {
    return consultations.find((consultation) => consultation.get('PrivateMainConcernClass') === ApiClientConstant.Regimen.Class.HAIR);
  }

  private async isConsultationEligibleForBanner(
    consultation: Table.ConsultationSession,
    experiment: ChatSkipLastQuestionsExperiment
  ): Promise<boolean> {
    const payload: RequestQueryPayload<Table.ConsultationSessionChat> = {
      where: {
        consultationSession: consultation,
        Owner: ApiClientConstant.ChatMessage.Owner.BOT,
      },
      limit: 1,
      project: ['Owner', 'questionId', 'objectId'],
      descending: 'createdAt',
    };
    const botQuestions = await this.getConsultationSessionChats(payload);
    const lastBotQuestion = botQuestions?.[0];
    return experiment?.variant?.questionIdsToSkipAt.includes(lastBotQuestion?.get('questionId'));
  }

  async getSubmitDefualtAnswersBannerItems(
    user: Table.User,
    fallbackCheckups?: InstantCheckup[]
  ): Promise<Array<{ consultation: Table.ConsultationSession; thumbnailUrl: string | null }>> {
    const experiment = this.experimentService.getChatSkipLastQuestionHomeBanner();
    if (!experiment?.enable) return [];

    const concernClasses = experiment.variant?.concernClasses;
    if (!concernClasses?.length) return [];

    const activeConsultations = await this.getConsultations(user);

    const items = (await Promise.all(
      concernClasses.map(async (concernClass) => {
        const consultation = activeConsultations.find(
          (c) => c.get('PrivateMainConcernClass') === concernClass
        );
        if (!consultation) return null;
        const eligible = await this.isConsultationEligibleForBanner(consultation, experiment);
        if (!eligible) return null;
        const thumbnailUrl = await this.getSubmitDefaultAnswersBannerThumbnail(consultation, concernClass, fallbackCheckups);
        return { consultation, thumbnailUrl };
      })
    ));

    return items.filter(Boolean);
  }

  async getSubmitDefaultAnswersBannerThumbnail(
    consultation: Table.ConsultationSession,
    consultationClass: string,
    fallbackCheckups?: InstantCheckup[]
  ): Promise<string | null> {
    const user = this.connectionService.getActingUser();
    if (!user) return null;

    if (consultationClass === ApiClientConstant.Regimen.Class.HAIR) {
      return this.getHairBannerThumbnail(user);
    }

    return this.getFaceBannerThumbnail(consultation, fallbackCheckups);
  }

  private async getHairBannerThumbnail(user: Table.User): Promise<string | null> {
    const checkups = await this.connectionService.fetchInstantCheckup({
      userId: user.get('username'),
      type: [
        ApiClientConstant.InstantCheckup.Type.HAIR_FRONT,
        ApiClientConstant.InstantCheckup.Type.HAIR_TOP,
      ],
      project: ['type', 'imagePath'],
      limit: 5,
      sortAscending: true,
    });

    if (!checkups?.length) return null;

    const hairFront = checkups.find(
      (checkup: InstantCheckup) => checkup.type === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT
    );
    const hairTop = checkups.find(
      (checkup: InstantCheckup) => checkup.type === ApiClientConstant.InstantCheckup.Type.HAIR_TOP
    );
    const preferred = hairFront || hairTop;
    return preferred?.imagePath || null;
  }

  private async getFaceBannerThumbnail(
    consultation: Table.ConsultationSession,
    fallbackCheckups?: InstantCheckup[]
  ): Promise<string | null> {
    const debugData = consultation.get('PrivateInstantCheckupDebug') as any;
    const sessionCheckups = debugData?.instantCheckups;

    const sideFaceTypes: string[] = [
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
    ];

    let checkups: InstantCheckup[];
    if (sessionCheckups?.length) {
      const ids = sessionCheckups.map((checkup: any) => checkup?.id).filter(Boolean);
      checkups = ids?.length ? await this.instantCheckupService.fetchInstantCheckupsByIds(ids) : [];
    } else {
      checkups = fallbackCheckups || [];
    }

    const frontFace = checkups.find((c) => c.type === ApiClientConstant.InstantCheckup.Type.FRONT_FACE);
    const sideFace = checkups.find((c) => sideFaceTypes.includes(c.type));
    let preferred = frontFace || sideFace;

    if (!preferred && fallbackCheckups?.length) {
      const fallbackFront = fallbackCheckups.find((c) => c.type === ApiClientConstant.InstantCheckup.Type.FRONT_FACE);
      const fallbackSide = fallbackCheckups.find((c) => sideFaceTypes.includes(c.type));
      preferred = fallbackFront || fallbackSide;
    }

    return preferred?.imagePath || null;
  }

  private isValidQuestionIdToSkip(questionId: string, variant: { questionIdsToSkipAt: string[] }): boolean {
    return variant?.questionIdsToSkipAt.includes(questionId);
  }
}
